import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Eye, EyeOff } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';

const schema = z
  .object({
    senha: z
      .string()
      .min(8, 'A senha deve ter pelo menos 8 caracteres')
      .regex(/[A-Za-z]/, 'A senha deve conter ao menos uma letra')
      .regex(/\d/, 'A senha deve conter ao menos um número'),
    confirmacao: z.string(),
  })
  .refine((d) => d.senha === d.confirmacao, {
    path: ['confirmacao'],
    message: 'As senhas não coincidem',
  });

type FormData = z.infer<typeof schema>;

export default function ContratacaoConvite() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showSenha, setShowSenha] = useState(false);
  const [showConf, setShowConf] = useState(false);
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  const onSubmit = async (data: FormData) => {
    if (!token) {
      toast.error('Convite inválido');
      return;
    }
    setLoading(true);
    try {
      const { data: resp, error } = await supabase.functions.invoke('contratacao-convite-aceitar', {
        body: { token, senha: data.senha },
      });

      if (error) {
        // Tentar ler mensagem real da Edge
        let message = 'Não foi possível aceitar o convite.';
        try {
          const ctx: any = (error as any).context;
          if (ctx && typeof ctx.json === 'function') {
            const body = await ctx.json();
            if (body?.message) message = body.message;
          } else if (ctx?.response && typeof ctx.response.json === 'function') {
            const body = await ctx.response.json();
            if (body?.message) message = body.message;
          } else if (error.message) {
            message = error.message;
          }
        } catch {
          // ignore
        }
        toast.error(message);
        return;
      }

      if (!resp?.access_token || !resp?.refresh_token) {
        toast.error(resp?.message || 'Resposta inválida do servidor.');
        return;
      }

      // Estabelecer sessão sem signIn manual
      const { error: erroSessao } = await supabase.auth.setSession({
        access_token: resp.access_token,
        refresh_token: resp.refresh_token,
      });
      if (erroSessao) {
        toast.error('Não foi possível iniciar a sessão. Tente novamente.');
        return;
      }

      toast.success('Senha definida com sucesso!');

      // Invalidar caches relevantes
      await queryClient.invalidateQueries({ queryKey: ['is-candidato-ativo'] });
      await queryClient.invalidateQueries({ queryKey: ['contratacao'] });

      // Transição controlada
      navigate('/candidato', { replace: true });
    } catch (err: any) {
      toast.error(err?.message || 'Erro inesperado.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary/20 via-background to-secondary/20 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-bold">Definir Senha</CardTitle>
          <CardDescription>
            Crie uma senha para acessar sua contratação. Este é o único caminho —
            mesmo que você já tenha conta, a senha será redefinida agora.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="senha">Nova senha</Label>
              <div className="relative">
                <Input
                  id="senha"
                  type={showSenha ? 'text' : 'password'}
                  placeholder="Mínimo 8 caracteres, com letra e número"
                  {...register('senha')}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowSenha((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={showSenha ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  {showSenha ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.senha && (
                <p className="text-sm text-destructive">{errors.senha.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmacao">Confirmar senha</Label>
              <div className="relative">
                <Input
                  id="confirmacao"
                  type={showConf ? 'text' : 'password'}
                  placeholder="Digite a senha novamente"
                  {...register('confirmacao')}
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowConf((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={showConf ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  {showConf ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.confirmacao && (
                <p className="text-sm text-destructive">{errors.confirmacao.message}</p>
              )}
            </div>

            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? 'Definindo...' : 'Definir senha e continuar'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
